import { getCountryParameter } from './common';

export interface IExternalAnalyticsConfig {
  provider?: string;
  modelName?: string;
  brand?: string;
}

export type KeyType =
  | 'Enter Configurator'
  | 'On Page Load'
  | 'Interactions'
  | 'Booking'
  | 'Booking Form'
  | 'View Spec'
  | 'Category Objects'
  | 'Choose Variant CTA'
  | 'Compare Variants';

export interface IValue {
  interactionType?: string;
  currentConfigurtor?: string;
  formType?: string;
  destination?: string;
  vehicleType?: string;
  modelName?: string;
  modelYear?: string;
  trimLevel?: string;
  bodyStyle?: string;
}

export interface IAdobeEvent {
  eventType: 'add_event' | 'start_event' | 'end_event';
  key: KeyType;
  value?: IValue; // Using IValue directly
}

export {};
declare global {
  interface Window {
    _satellite: any;
    externalAnalyticsGM: any;
    externalAnalytics: any;
    digitalData: any;
  }
}

export enum DataDtm {
  LeadForms = 'Lead Forms',
  Cart = 'cart',
  ConfiguratorLanding = 'Configurator Landing',
  Fullscreen = 'fullscreen',
  Language = 'language',
  ViewChange = 'View Change',
  AmbientLighting = 'Ambient Lighting',
  BackgroundChange = 'Background Change',
  ShowVariantChange = 'Show Variant Change',
  ThreeDHotspotDropdown = '3d hotspot dropdown',
  SwitchView = 'switch view',
  SpecDetails = 'Spec details',
  Disclaimer = 'Disclaimer',
  TermsAndConditions = 'Terms and Conditions',
  // Left icon identifiers
  Interactions = 'interactions',
  Features = 'features',
  Share = 'share',
  Background = 'background',

  // Legacy (metadome-autodome-web) values for compatibility
  PresentationLanding = 'presentation-landing',
  ConfiguratorLandingLegacy = 'configurator-landing',
  SwitchViewLegacy = 'switch-view',
  TermsAndConditionsLegacy = 'terms-and-conditions',
  DisclaimerLegacy = 'disclosure',
  SpecDetailsLegacy = 'spec-details',
  ShareButton = 'share-button',
  ShareOptions = 'share-options',
  ThreeDHotspotDropdownLegacy = '3d-hotspot-dropdown',
  FullScreenButton = 'full-screen-button',
  LanguageToggle = 'language-toggle',
  AmbientLightPicker = 'ambient-light-picker',
  EnterConfigurator = 'enter-configurator',
  CartCTA = 'cart-cta',
  CloseButton = 'close-button',
  SubmitBookingForm = 'submit-booking-form',
  SendOffersCheckboxBookingForm = 'send-offers-checkbox-booking-form',
  PrivacyPolicyCheckboxBookingForm = 'privacy-policy-checkbox-booking-form',
  EquinoxEV = 'equinox ev',
  NavSecondary = 'nav secondary',
  Summary = 'summary',
  YourInfo = 'your info',
  Submit = 'submit',
  PrivacyStatement = 'privacy statement',
  BackgroundChangeLegacy = 'background-change',
}

export enum StatLinkClassProp {
  statbuttonlink = 'stat-button-link',
  stattablink = 'stat-tab-link',
  statdropdown = 'stat-dropdown',
  statbuttonclose = 'stat-button-close',
  statsubmitbutton = 'stat-button-valid',
  statcheckbox = 'stat-checkbox',
  statTextLink = 'stat-text-link',
  statImageLink = 'stat-image-link',
  statInputField = 'stat-input-field',
  statDisclaim = 'stat-disclaim',
}

// Map UI button/sub-option ids to DataDtm site section strings
export const SiteSectionIdsMap: Record<string, DataDtm | string> = {
  // Floating action buttons
  expand: DataDtm.Fullscreen,
  interactions: DataDtm.Interactions,
  highlights: DataDtm.Features,
  color: DataDtm.AmbientLighting,
  // More options tray items
  switch_language: DataDtm.Language,
  change_background: DataDtm.Background,
  share_configuration: DataDtm.Share,
};

export const updateGMDigitalDataSiteSection = (value: string) => {
  window.externalAnalyticsGM?.updateSiteSection(value);
};

export const updateGMColorEVar = (
  view: 'exterior' | 'interior',
  value: string
) => {
  window.externalAnalyticsGM?.changeColorEVar(view, value);
};

const defaultTime = { start: 0, end: 0 };

export class ExternalAnalyticsGM {
  private initialized = false;
  private carModel = '';
  private vehicleId = '';
  private brand = '';
  private config: any = {};

  private trackEventsMapping = {
    assetsLoaded: 'assets-loaded',
    configuratorStart: 'config-start',
    configuratorStepLoad: 'config-step-load',
    configuratorThankYou: 'config-ty',
  };

  init(
    serviceProvider: IExternalAnalyticsConfig | undefined,
    modelName: string,
    config: any
  ) {
    const provider = serviceProvider?.provider || '';
    const model = serviceProvider?.modelName || modelName;
    const brand = serviceProvider?.brand || '';
    this.config = config;

    if (provider === 'adobe') {
      this.initAdobe();
      this.initialized = true;
      this.carModel = model;
      this.brand = brand;
      this.vehicleId = this.config.vehicleId || '';
      this.initDigitalData();
      // Register globals for analytics access (GM + legacy alias)
      window.externalAnalyticsGM = this;
      window.externalAnalytics = this;
    }
  }

  sendTrackEvent(key: string) {
    if (window._satellite) {
      window._satellite.track(
        this?.trackEventsMapping?.[key as keyof typeof this.trackEventsMapping]
      );
    }
  }

  // Adobe's own pageBottom hook - fires the tag manager's end-of-page beacon.
  pageBottom() {
    if (window._satellite) {
      window._satellite.pageBottom();
    }
  }

  // Presentation (landing) page-load beacon. eVars must already be set by
  // addInitialEvars(); this just flushes them via the AppMeasurement instance.
  presentationPageLoad() {
    const s = this.satelliteInstance();
    if (s) {
      s.ta();
    }
  }

  // Fires on the lead form's thank-you step, in place of a step-load beacon.
  configuratorThankYou() {
    if (!this.initialized) return;
    this.sendTrackEvent('configuratorThankYou');
  }

  updateSiteSection(value: string) {
    if (this.initialized && window.digitalData && window.digitalData.pageInfo) {
      window.digitalData.pageInfo.siteSectionsLevel3 = value;
      window.digitalData.pageInfo.siteSectionsLevel5 = `configurator:2025-${this.carModel}:'${value}`;
    }
  }

  private initAdobe() {
    // if (window._satellite) {
    //   window._satellite.pageBottom()
    // }
  }

  private getCountryValues() {
    const country = getCountryParameter();
    if (country === 'UAE') {
      return { code: 'ae', name: 'united arab emirates' };
    } else if (country === 'KSA') {
      return { code: 'sa', name: 'saudi arabia' };
    } else if (country === 'KW') {
      return { code: 'kw', name: 'kuwait' };
    }
    return { code: 'ae', name: 'united arab emirates' };
  }

  private initDigitalData() {
    const ua = navigator.userAgent.toLowerCase();
    const { code, name } = this.getCountryValues();
    let renderedExperience = '';
    if (/mobile|iphone|ipod|android.*mobile|windows phone/.test(ua)) {
      renderedExperience = 'small';
    } else if (/ipad|tablet|android(?!.*mobile)/.test(ua)) {
      renderedExperience = 'medium';
    } else {
      renderedExperience = 'large';
    }
    let $orientation = '';
    if (window.innerHeight > window.innerWidth) {
      $orientation = 'portrait';
    } else {
      $orientation = 'landscape';
    }
    const vw = Math.max(
      document.documentElement.clientWidth || 0,
      window.innerWidth || 0
    );
    const vh = Math.max(
      document.documentElement.clientHeight || 0,
      window.innerHeight || 0
    );

    const digitalData: any = {
      pageInfo: {},
      vehicleInfo: {},
    };
    digitalData.pageInfo = {
      pageName: `ch:me:${code}:ar:t1:configurator:2025-${this.carModel}`,
      siteSectionsLevel1: 'configurator',
      siteSectionsLevel2: `2025-${this.carModel}`,
      siteSectionsLevel3: 'Presentation-Landing',
      siteSectionsLevel4: '',
      siteSectionsLevel5: `configurator:2025-${this.carModel}:Presentation-Landing`,
      url: '',
      pageType: 'tool',
      pageSubType: '',
      pageMarketing: '',
      pageMarketingSubType: '',
      languageSelected: 'english || arabic',
      siteName: 'microsite_configurator',
      brand: this.brand,
      country: name,
      region: 'middle east',
      renderedExperience,
      viewport: vw + 'x' + vh,
      orientation: $orientation,
    };
    digitalData.vehicleInfo = {
      vehicleType: 'car',
      modelName: `${this.carModel}`,
      modelYear: '2025',
      bodyStyle: `${this.carModel}`,
      trimLevel: 'coupe',
    };
    window.digitalData = digitalData;
  }

  private satelliteInstance() {
    if (window._satellite) {
      return window._satellite.getToolsByType('sc')[0].getS();
    } else {
      return null;
    }
  }

  private setVar(key: string, value: string) {
    if (window._satellite) {
      return window._satellite.setVar(key, value);
    }
  }

  changeColorEVar(view: 'exterior' | 'interior', value: string) {
    if (view === 'exterior') {
      this.setVar('eVar13', value);
    } else {
      this.setVar('eVar14', value);
    }
  }

  addInitialEvars() {
    if (!window._satellite) return;

    // Top-level page variables
    this.setVar(
      'pageName',
      `ch:me:ae:ar:t1:configurator:2025-${this.carModel}`
    );
    this.setVar('channel', 'configurator');
    this.setVar('prop41', `ch:me:ae:ar:t1:configurator:2025-${this.carModel}`);
    this.setVar('eVar37', `ch:me:ae:ar:t1:configurator:2025-${this.carModel}`);

    // Site section levels
    this.setVar('prop11', `2025-${this.carModel}`);
    this.setVar('prop12', 'trim');
    this.setVar('prop13', 'undefined');
    this.setVar('prop14', `configurator:2025-${this.carModel}:trim`);

    // URL and page type info
    const host = window.location.host;
    this.setVar('eVar83', host);
    this.setVar('prop67', host);

    this.setVar('eVar76', 'tool');
    this.setVar('prop60', 'tool');

    this.setVar('eVar77', 'presentation');
    this.setVar('prop61', 'presentation');

    this.setVar('eVar78', '');
    this.setVar('prop62', '');

    this.setVar('eVar79', '');
    this.setVar('prop63', '');

    // Language
    this.setVar('eVar4', 'english || arabic');
    this.setVar('prop23', 'english || arabic');

    // Site and brand
    this.setVar('eVar17', 'microsite_configurator');
    this.setVar('prop17', 'microsite_configurator');

    this.setVar('eVar18', `${this.brand}`);
    this.setVar('prop18', `${this.brand}`);

    // Country and region
    this.setVar('eVar31', 'all middle east countries');
    this.setVar('eVar32', 'middle east');

    // Device/rendering info
    this.setVar('eVar96', 'large');
    this.setVar('prop71', 'large');

    this.setVar('eVar97', '1186x947');
    this.setVar('prop74', '1186x947');

    this.setVar('eVar98', 'landscape');
    this.setVar('prop75', 'landscape');

    // Vehicle data
    this.setVar('eVar7', 'car');
    this.setVar('prop3', 'car');

    this.setVar('eVar5', `${this.carModel}`);
    this.setVar('prop1', `${this.carModel}`);

    this.setVar('eVar28', '2025');
    this.setVar('prop37', '2025');

    this.setVar('eVar6', `${this.carModel}`);
    this.setVar('prop2', `${this.carModel}`);

    this.setVar('eVar26', 'coupe');
    this.setVar('eVar13', 'rapid-blue');
    this.setVar('eVar14', 'jet-black_perforated-leather-appointed');
  }

  sendEvent({ eventType, key, value }: IAdobeEvent) {
    return '';
  }
}
